import Grid from '@mui/material/Grid';
import { errorMessage } from 'constants/CommonConstants';
import { atLeastOneFieldValidationRule } from 'helper';
import React, { useImperativeHandle } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CommonInput } from 'sharedComponents/inputGroup';
import { Lead } from '../LeadGenInterface';
import { postLeadCreation } from 'store/LeadCreationState';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { RootState } from 'store/store';
import { useDispatch, useSelector } from 'react-redux';
import { StepperFromProps } from 'sharedComponents/dynamicStepperForm/dynamicStepperFormInterface';

const YearsOfIncome: React.ForwardRefRenderFunction<unknown, StepperFromProps> = (
  { onNext, onBack, isSubmit, mappedData },
  ref
) => {
  // Hooks
  const { t } = useTranslation();

  const hasCalledNextStep = React.useRef(false);

  // Redux
  const dispatch: ThunkDispatch<RootState, AnyAction, AnyAction> = useDispatch();

  const { authenticateClientData } = useSelector((state: RootState) => state.auth) || {};
  const widgetData = authenticateClientData?.data?.widgetData || {};

  // React Hook Form
  const { control, trigger, getValues } = useFormContext();

  // React Hook From getValues
  const { incomePerMonthBeforeTax, employmentStatus, yearsAgoStartedReceivingIncome, monthsAgoStartedReceivingIncome } =
    getValues();

  // React Hook Form Trigger
  const nextStep = async () => {
    if (hasCalledNextStep.current) return;
    hasCalledNextStep.current = true;

    const yearsOfIncomeData: Lead = {
      leadSourceId: widgetData?.leadSourceId,
      isSubmit,
      leadDetails: {
        'Years Ago Started Receiving Income': yearsAgoStartedReceivingIncome,
        'Months Ago Started Receiving Income': monthsAgoStartedReceivingIncome,
        ...mappedData,
      },
    };

    dispatch(postLeadCreation({ data: yearsOfIncomeData, leadTypeID: widgetData?.leadTypeId })).then((res) => {
      if (
        res?.payload?.success &&
        (res?.payload?.data?.draftLeadId || res?.payload?.data?._id || res?.payload?.data?.Lead?.leadId)
      ) {
        const addressStepIndex = widgetData?.stepConfig?.indexOf('AddressStep');
        employmentStatus === 'Employed' ? onNext?.() : addressStepIndex > -1 ? onNext?.(addressStepIndex) : onNext?.(); // 9 AddressStep;
      }
    });
  };

  const backStep = () => {
    const taxDeductionStepIndex = widgetData?.stepConfig?.indexOf('TaxDeductionStep');

    incomePerMonthBeforeTax >= 1800
      ? taxDeductionStepIndex > -1
        ? onBack?.(taxDeductionStepIndex) // 5 TaxDeductionStep
        : onBack?.()
      : onBack?.();
  };

  useImperativeHandle(ref, () => ({
    nextStep,
    backStep,
  }));

  return (
    <>
      <h3 className='dy-stepper-form__body__title text-center'>{t('leadGen.yearOfIncomeTxt1')}</h3>
      <Grid container justifyContent={{ xs: 'left', sm: 'center' }} rowSpacing={3} columnSpacing={2}>
        <Grid item sm={6} xs={12}>
          <CommonInput
            name='yearsAgoStartedReceivingIncome'
            control={control}
            label={t('leadGen.years')}
            placeholder={t('leadGen.enterYear')}
            rules={{
              ...atLeastOneFieldValidationRule('monthsAgoStartedReceivingIncome', errorMessage(t).yearsRequired),
            }}
            triggerValidationForField={trigger}
            type='number'
          />
        </Grid>
        <Grid item sm={6} xs={12}>
          <CommonInput
            name='monthsAgoStartedReceivingIncome'
            control={control}
            label={t('leadGen.month')}
            placeholder={t('leadGen.enterMonth')}
            rules={{
              ...atLeastOneFieldValidationRule('yearsAgoStartedReceivingIncome', errorMessage(t).monthsRequired),
            }}
            triggerValidationForField={trigger}
            type='number'
          />
        </Grid>
      </Grid>
    </>
  );
};

const YearsOfIncomeStep = React.forwardRef(YearsOfIncome);

export default YearsOfIncomeStep;
